/**
 * WebABTestFolderService
 *
 * Manages WebABTestFolder entities for organizing Web A/B tests into folders.
 *
 * Structure:
 * - projects/{projectId}/webABTestFolders/{folderId}
 *
 * Pattern: Copied from ABTestFolderService.ts (iOS A/B tests)
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  orderBy,
  FirestoreDataConverter,
} from 'firebase/firestore';
import { CoreFirebaseService } from './CoreFirebaseService';
import {
  WebABTestFolder,
  webABTestFolderConverter,
} from '../converters/webABTestFolderConverter';

/**
 * Input type for creating a new folder
 */
export interface CreateWebABTestFolderInput {
  name: string;
  parentFolderId?: string | null;
}

/**
 * Input type for updating a folder
 */
export interface UpdateWebABTestFolderInput {
  name?: string;
}

/**
 * WebABTestFolderService
 *
 * Provides CRUD operations for Web A/B test folders
 */
export class WebABTestFolderService {
  private readonly collectionName = 'webABTestFolders';
  private readonly firestore = CoreFirebaseService.getInstance().firestore;
  private readonly converter: FirestoreDataConverter<WebABTestFolder>;

  constructor(converter: FirestoreDataConverter<WebABTestFolder> = webABTestFolderConverter) {
    this.converter = converter;
  }

  /**
   * Create a new folder
   *
   * @param projectId - Project ID
   * @param data - Folder data (name, optional parentFolderId)
   * @returns Created folder with generated id
   */
  async create(projectId: string, data: CreateWebABTestFolderInput): Promise<WebABTestFolder> {
    try {
      const collectionRef = collection(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`
      );
      const docRef = doc(collectionRef).withConverter(this.converter);

      const now = new Date().toISOString();
      const folderData: WebABTestFolder = {
        id: docRef.id,
        projectId,
        name: data.name,
        parentFolderId: data.parentFolderId ?? null,
        createdAt: now,
        updatedAt: now,
      };

      await setDoc(docRef, folderData);
      return folderData;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebABTestFolderService.create');
      throw error;
    }
  }

  /**
   * Get a folder by ID
   *
   * @param projectId - Project ID
   * @param folderId - Folder ID
   * @returns Folder or null if not found
   */
  async get(projectId: string, folderId: string): Promise<WebABTestFolder | null> {
    try {
      const docRef = doc(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`,
        folderId
      ).withConverter(this.converter);

      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return { ...docSnap.data(), id: docSnap.id };
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebABTestFolderService.get');
      throw error;
    }
  }

  /**
   * List all folders for a project
   *
   * @param projectId - Project ID
   * @returns Array of folders ordered by name
   */
  async list(projectId: string): Promise<WebABTestFolder[]> {
    try {
      const collectionRef = collection(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`
      ).withConverter(this.converter);

      const q = query(collectionRef, orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebABTestFolderService.list');
      throw error;
    }
  }

  /**
   * Update a folder
   *
   * @param projectId - Project ID
   * @param folderId - Folder ID
   * @param updates - Fields to update
   */
  async update(projectId: string, folderId: string, updates: UpdateWebABTestFolderInput): Promise<void> {
    try {
      const docRef = doc(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`,
        folderId
      ).withConverter(this.converter);

      const updateData = {
        ...updates,
        updatedAt: new Date().toISOString(),
      };

      await setDoc(docRef, updateData as WebABTestFolder, { merge: true });
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebABTestFolderService.update');
      throw error;
    }
  }

  /**
   * Delete a folder
   *
   * NOTE: This does not cascade delete A/B tests in the folder.
   * The UI should handle moving or warning about nested A/B tests.
   *
   * @param projectId - Project ID
   * @param folderId - Folder ID to delete
   */
  async delete(projectId: string, folderId: string): Promise<void> {
    try {
      const docRef = doc(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`,
        folderId
      );

      await deleteDoc(docRef);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebABTestFolderService.delete');
      throw error;
    }
  }
}
